import screenfull from "screenfull";

export type FullscreenMode = "api" | "immersive" | "unavailable";

export interface RequestFullscreenResult {
  mode: FullscreenMode;
  /** True when the Fullscreen API is active (not CSS immersive). */
  isApiFullscreen: boolean;
}

function getFullscreenTarget(): HTMLElement {
  return document.documentElement;
}

/**
 * Enter fullscreen via screenfull when supported. On iPhone and other
 * environments where the Fullscreen API is unavailable, returns
 * `{ mode: 'immersive' }` so the UI can rely on full-viewport + safe-area layout.
 *
 * Call from a direct user gesture (e.g. button onClick). Browsers reject
 * requestFullscreen when invoked from useEffect/async after the gesture ends.
 */
export async function requestFullscreen(): Promise<RequestFullscreenResult> {
  if (typeof document === "undefined") {
    return { mode: "unavailable", isApiFullscreen: false };
  }

  if (!screenfull.isEnabled) {
    return { mode: "immersive", isApiFullscreen: false };
  }

  const target = getFullscreenTarget();

  try {
    await screenfull.request(target);
    return { mode: "api", isApiFullscreen: screenfull.isFullscreen };
  } catch {
    return { mode: "immersive", isApiFullscreen: false };
  }
}

function isDocumentFullscreen(): boolean {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  return document.fullscreenElement != null || doc.webkitFullscreenElement != null;
}

async function exitFullscreenNative(): Promise<void> {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    mozCancelFullScreen?: () => Promise<void> | void;
    msExitFullscreen?: () => Promise<void> | void;
  };

  try {
    const p = document.exitFullscreen?.();
    if (p && typeof (p as Promise<void>).then === "function") {
      await p;
      return;
    }
  } catch {
    /* try prefixed */
  }

  try {
    await doc.webkitExitFullscreen?.();
  } catch {
    /* ignore */
  }
  try {
    await doc.mozCancelFullScreen?.();
  } catch {
    /* ignore */
  }
  try {
    await doc.msExitFullscreen?.();
  } catch {
    /* ignore */
  }
}

/**
 * Leave browser fullscreen. screenfull first, then native APIs if still full.
 */
export async function exitFullscreen(): Promise<void> {
  if (typeof document === "undefined") return;

  try {
    if (
      "exit" in screenfull &&
      typeof screenfull.exit === "function" &&
      screenfull.isEnabled &&
      screenfull.isFullscreen
    ) {
      await screenfull.exit();
    }
  } catch {
    /* fall through */
  }

  if (isDocumentFullscreen()) {
    try {
      await exitFullscreenNative();
    } catch {
      /* ignore */
    }
  }
}

export function isFullscreenApiEnabled(): boolean {
  return typeof document !== "undefined" && screenfull.isEnabled;
}

/** Whether document fullscreen is currently active (when API exists). */
export function isFullscreenActive(): boolean {
  if (typeof document === "undefined" || !screenfull.isEnabled) {
    return false;
  }
  return Boolean(screenfull.isFullscreen);
}

export function subscribeFullscreenChange(
  listener: (isFullscreen: boolean) => void
): () => void {
  if (typeof document === "undefined" || !screenfull.isEnabled) {
    return () => {};
  }
  const onChange = () => {
    listener(screenfull.isFullscreen);
  };
  screenfull.on("change", onChange);
  return () => {
    screenfull.off("change", onChange);
  };
}
